import { IconType } from "react-icons";

import { tw } from "utils/tw";

type BadgeSize = "sm" | "lg";
export type BadgeVariant =
  | "default"
  | "default_outlined"
  | "silver"
  | "pending"
  | "success"
  | "warning"
  | "error";

interface Props {
  size?: BadgeSize;
  variant?: BadgeVariant;
  LeadingIcon?: IconType;
  text?: string;
  isSquare?: boolean;
}

export default ({
  variant = "default",
  size = "sm",
  LeadingIcon,
  text,
  isSquare,
}: React.PropsWithChildren<Props>): React.JSX.Element => {
  const base = tw(
    "inline-flex",
    "space-x-1",
    "items-center",
    "justify-center",
    "text-sm",
    "font-semibold",
    "text-deepBlue-900",
    "px-3",
    {
      "py-0.5": size === "sm",
      "py-2": size === "lg",
      rounded: !isSquare,
    }
  );

  const badgeStyles = (() => {
    switch (variant) {
      case "default":
        return tw(base, "bg-deepBlue-50");

      case "default_outlined":
        return tw(base, "border", "border-deepBlue-100");

      case "silver":
        return tw(base, "bg-silver");

      case "pending":
        return tw(base, "bg-blue-100");

      case "success":
        return tw(base, "bg-badge-success");

      case "warning":
        return tw(base, "bg-badge-warning");

      case "error":
        return tw(base, "bg-badge-error");

      default:
        return base;
    }
  })();

  const iconSize = (() => {
    switch (size) {
      case "sm":
        return 12;

      case "lg":
        return 16;

      default:
        return undefined;
    }
  })();

  return (
    <div className={badgeStyles} data-testid="badge">
      {LeadingIcon && (
        <LeadingIcon
          size={iconSize}
          data-testid="leading-icon"
          className={tw("shrink-0")}
        />
      )}

      {text && <span>{text}</span>}
    </div>
  );
};
